import { logClientInfo } from '../../actions/logging';
import { discountTypes } from '../../constants/subscription/coupon';
import { isProdEnv } from '../../utils/environment';
import isServer from '../../utils/isServer';
import billingFrequency from '../../utils/subscription/billingPeriod';
import formatCents from '../../utils/subscription/formatCents';

// Use for logging purposes.
// Context from ticket TUNE-17132: User was able to use $50 OFF Once (0oA7DER7) coupon with monthly sub (coupon should be used only with 9999_0days_yearly)
// Source: https://tunein.atlassian.net/browse/TUNE-17132
// TODO: let's follow-up on logs and discover how to gate this coupon's applicability properly, then we can delete this and the associated logs
const VERY_BAD_COUPON = '0oA7DER7';

export interface CouponLogger {
  dispatch?(action: unknown): unknown;
  loggerInfo?(payload: unknown): void;
}

interface CouponLogData extends Record<string, unknown> {
  couponData?: { code?: string } | null;
}

export interface CouponDiscountableProduct extends Record<string, unknown> {
  price: number;
  currencyCode: string;
  formattedPrice?: string;
  currencies?: Record<string, number>;
  billingPeriod?: string;
  sku?: string;
  stripeProduct?: { id?: string | null } | null;
  defaultCouponCode?: string;
  isUsingDefaultCoupon?: boolean;
  isFree?: boolean;
  formattedDiscountedPrice?: string;
  discountedCurrencies?: Record<string, number>;
  discountFreeTrialLength?: number;
  discountFreeTrialUnit?: string;
  discountDurationType?: string;
  discountDurationInMonths?: number;
}

type CurrencyUpdater = (value: number, currencyCode: string) => number;

function buildCurrencyMap(
  currencies: Record<string, number> | undefined,
  computeValue: CurrencyUpdater,
): Record<string, number> {
  if (!currencies) {
    return {};
  }

  return Object.entries(currencies).reduce<Record<string, number>>((result, [code, value]) => {
    result[code] = computeValue(value, code);
    return result;
  }, {});
}

function logApplyCouponDiscountInfo(
  logger: CouponLogger | undefined,
  message: string,
  data: CouponLogData,
) {
  if (!isProdEnv() || data?.couponData?.code !== VERY_BAD_COUPON) {
    return;
  }

  if (logger?.dispatch) {
    logger.dispatch(
      logClientInfo({
        message,
        context: data,
      }),
    );
    return;
  }

  if (isServer() && logger?.loggerInfo) {
    logger.loggerInfo({
      message,
      meta: data,
    });
  }
}

export function applyCouponDiscount<TProduct extends CouponDiscountableProduct>(
  product: TProduct,
  couponData: Record<string, any>,
  isUsingDefaultCoupon = false,
  logger?: CouponLogger,
): TProduct {
  if (couponData.durationInMonths && product.billingPeriod === billingFrequency.yearly) {
    logApplyCouponDiscountInfo(logger, 'applyCouponDiscount.canceledDueToDurationInMonths', {
      couponData,
      product,
    });
    return product;
  }

  if (
    couponData.appliesToAllPlans ||
    couponData.plans?.includes(product.sku) ||
    couponData.plans?.includes(product?.stripeProduct?.id)
  ) {
    const newProduct: TProduct = {
      ...product,
      isUsingDefaultCoupon,
    };

    if (couponData.type === discountTypes.percent) {
      const percentage = 1 - couponData.percentage / 100;

      if (percentage === 0) {
        newProduct.isFree = true;
      }

      newProduct.formattedDiscountedPrice = formatCents(
        Math.ceil(newProduct.price * percentage),
        newProduct.currencyCode,
      );

      newProduct.discountedCurrencies = buildCurrencyMap(newProduct.currencies, (value) =>
        Math.ceil(value * percentage),
      );
    } else if (couponData.type === discountTypes.dollars) {
      const discountedCentsForCurrency = couponData.centsCurrencyMap?.[newProduct.currencyCode];
      const newPrice = discountedCentsForCurrency
        ? newProduct.price - discountedCentsForCurrency
        : newProduct.price;

      if (newPrice === 0) {
        newProduct.isFree = true;
      }

      newProduct.formattedDiscountedPrice = discountedCentsForCurrency
        ? formatCents(newPrice, newProduct.currencyCode)
        : newProduct.formattedPrice;

      newProduct.discountedCurrencies = buildCurrencyMap(newProduct.currencies, (value, key) => {
        const discountedCents = couponData.centsCurrencyMap?.[key];
        return discountedCents ? value - discountedCents : value;
      });
    } else if (couponData.type === discountTypes.freeTrial) {
      newProduct.discountFreeTrialLength = couponData.freeTrialAmount;
      newProduct.discountFreeTrialUnit = couponData.freeTrialUnit;
    }

    newProduct.discountDurationType = couponData.durationType;
    if (couponData.durationInMonths) {
      newProduct.discountDurationInMonths = couponData.durationInMonths;
    }

    logApplyCouponDiscountInfo(logger, 'applyCouponDiscount.done', {
      couponData,
      product,
      newProduct,
    });

    return newProduct;
  }

  logApplyCouponDiscountInfo(
    logger,
    'applyCouponDiscount.canceledDueToCouponDoesNotApplyToAnyProduct',
    {
      couponData,
      product,
    },
  );

  return product;
}

export async function fetchAndApplyDefaultCouponDiscount<
  TProduct extends CouponDiscountableProduct,
>(
  product: TProduct,
  fetchCouponByCode: (product: TProduct) => Promise<Record<string, any> | null>,
  logger?: CouponLogger,
): Promise<TProduct> {
  if (!product.defaultCouponCode) {
    return product;
  }

  try {
    const couponData = await fetchCouponByCode(product);
    if (!couponData) {
      return product;
    }

    return applyCouponDiscount(product, couponData, true, logger);
  } catch {
    return product;
  }
}

interface DecorateProductsOptions<TProduct extends CouponDiscountableProduct> {
  products: TProduct[];
  couponFromState?: Record<string, any> | null;
  fetchDefaultCoupon: ((product: TProduct) => Promise<Record<string, unknown> | null>) | null;
  logger?: CouponLogger;
}

export async function decorateProductsWithCoupons<TProduct extends CouponDiscountableProduct>({
  products,
  couponFromState,
  fetchDefaultCoupon,
  logger,
}: DecorateProductsOptions<TProduct>): Promise<TProduct[]> {
  if (!Array.isArray(products) || products.length === 0) {
    return [];
  }

  if (couponFromState?.type) {
    return products.map((product) =>
      applyCouponDiscount(product, couponFromState, couponFromState.isProductDefaultCoupon, logger),
    );
  }

  if (!fetchDefaultCoupon) {
    return products;
  }

  return Promise.all(
    products.map((product) =>
      fetchAndApplyDefaultCouponDiscount(product, fetchDefaultCoupon, logger),
    ),
  );
}
